import React from 'react';
import type { CampaignEditRequest, FieldChange } from '@discovr/types';
import { Field, Pill, PillField } from './campaignDetailsParts';
import { CommentThreadPopover } from './CommentThreadPopover';
import { formatScalar, formatFieldValueFor, diffKind, isSelfApprovedByAdmin } from './editRequestFormat';

/** Which changes to show inline on the read layout: pending and rejected
 *  always; approved only while unapplied, unless `includeApplied` (a history
 *  view needs the full record including what shipped). */
function isDiffVisible(c: FieldChange, includeApplied: boolean): boolean {
  if (c.status === 'pending' || c.status === 'rejected') return true;
  if (c.status === 'approved') return includeApplied || !c.applied_at;
  // Unknown status — surface the field rather than silently dropping it.
  console.warn(`editRequestDiff: unrecognized FieldChange status "${String(c.status)}" for field "${c.field_path}" — showing it rather than hiding it.`);
  return true;
}

/** field_path → FieldChange for every diffable change, for O(1) lookup as
 *  CampaignDetailsTab renders. `includeApplied: true` for a past-request
 *  history view; false for the admin's live current-request overlay. */
export function buildDiffMap(
  editRequest: CampaignEditRequest | null | undefined,
  opts?: { includeApplied?: boolean },
): Map<string, FieldChange> {
  const map = new Map<string, FieldChange>();
  if (!editRequest) return map;
  for (const c of editRequest.changes) {
    if (isDiffVisible(c, opts?.includeApplied ?? false)) map.set(c.field_path, c);
  }
  return map;
}

/** field_path -> every resolved FieldChange for that path across past requests
 *  except `currentRequestId`, newest-first (relies on `historyRequests` already
 *  being newest-first). Feeds CommentThreadPopover's "Change X/N" pager. */
export function buildFieldHistoryMap(
  historyRequests: CampaignEditRequest[],
  currentRequestId: string,
): Map<string, FieldChange[]> {
  const map = new Map<string, FieldChange[]>();
  for (const req of historyRequests) {
    if (req.id === currentRequestId) continue;
    for (const c of req.changes) {
      if (c.status === 'pending') continue; // only resolved entries count as "history"
      const list = map.get(c.field_path) ?? [];
      list.push(c);
      map.set(c.field_path, list);
    }
  }
  return map;
}

// Review-status pill: amber pending, emerald approved, rose rejected. The only
// pill on a diffed field's label row — change-kind is conveyed by color instead.
const REVIEW_STATUS_STYLE: Record<FieldChange['status'], string> = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
};
// Fallback style/label for a status outside the known union at runtime.
const UNKNOWN_REVIEW_STATUS_STYLE = 'bg-slate-100 text-slate-700';
const UNKNOWN_REVIEW_STATUS_LABEL = 'Unknown';
// Brand sees "Requested" for pending (they asked for it); admin keeps
// "Pending" to match the review sidebar's tab.
const REVIEW_STATUS_LABEL: Record<'admin' | 'brand', Record<FieldChange['status'], string>> = {
  admin: { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' },
  brand: { pending: 'Requested', approved: 'Approved', rejected: 'Rejected' },
};

export function ReviewStatusBadge({
  status,
  audience = 'admin',
  submittedByRole,
}: {
  status: FieldChange['status'];
  audience?: 'admin' | 'brand';
  /** When set, an 'approved' status submitted by admin reads "Applied by admin"
   *  instead of "Approved" (admin edits are self-approved, not reviewed). */
  submittedByRole?: string;
}) {
  // Status came off the wire, so fall back defensively if it's outside the union
  // (isDiffVisible already warns once per unrecognized change).
  const style = REVIEW_STATUS_STYLE[status] ?? UNKNOWN_REVIEW_STATUS_STYLE;
  const label = isSelfApprovedByAdmin({ status, submitted_by_role: submittedByRole })
    ? 'Applied by admin'
    : (REVIEW_STATUS_LABEL[audience][status] ?? UNKNOWN_REVIEW_STATUS_LABEL);
  return (
    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium tracking-wide uppercase ${style}`}>
      {label}
    </span>
  );
}

/** Scalar/text old→new — struck-through old, bold new, for every non-array
 *  field. `path` drives the same formatting the field's card uses. */
export function DiffValue({ path, oldValue, newValue, currency }: { path: string; oldValue: unknown; newValue: unknown; currency?: string | null }) {
  const kind = diffKind(oldValue, newValue);
  // added/removed show just the one value that exists — clearer than
  // "— → new" or "old → —".
  if (kind === 'added') {
    return (
      <span className="inline-flex items-center rounded-md border border-dashed border-border px-2 py-0.5 font-medium text-muted-foreground">
        {formatFieldValueFor(path, newValue, currency)}
      </span>
    );
  }
  if (kind === 'removed') {
    return (
      <span className="inline-flex items-center rounded-md px-2 py-0.5 text-muted-foreground line-through">
        {formatFieldValueFor(path, oldValue, currency)}
      </span>
    );
  }
  return (
    <span>
      <span className="text-muted-foreground line-through">{formatFieldValueFor(path, oldValue, currency)}</span>
      {' → '}
      <span className="font-medium text-foreground">{formatFieldValueFor(path, newValue, currency)}</span>
    </span>
  );
}

/** Array-field old→new as one pill row: kept normal, removed struck-through,
 *  added dashed. Values compare raw but display through the path's formatter
 *  ("10K – 50K" / "USA", not stored "10000-50000" / "usa"). */
export function DiffPills({ path, oldValue, newValue, currency }: { path: string; oldValue: unknown; newValue: unknown; currency?: string | null }) {
  const oldArr = Array.isArray(oldValue) ? oldValue.map(String) : [];
  const newArr = Array.isArray(newValue) ? newValue.map(String) : [];
  const newSet = new Set(newArr);
  const oldSet = new Set(oldArr);
  const kept = oldArr.filter((v) => newSet.has(v));
  const removed = oldArr.filter((v) => !newSet.has(v));
  const added = newArr.filter((v) => !oldSet.has(v));
  const fmt = (v: string) => formatScalar(path, v, currency);

  if (kept.length === 0 && removed.length === 0 && added.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {kept.map((v, i) => (
        <Pill key={`kept-${v}-${i}`}>{fmt(v)}</Pill>
      ))}
      {removed.map((v, i) => (
        <Pill key={`removed-${v}-${i}`} variant="removed">
          {fmt(v)}
        </Pill>
      ))}
      {added.map((v, i) => (
        <Pill key={`added-${v}-${i}`} variant="added">
          {`+ ${fmt(v)}`}
        </Pill>
      ))}
    </div>
  );
}

/** Looks up path's diffable change and pre-builds its badge/value/pills/
 *  commentDot renderables (a plain lookup, not a hook). `requestId` is the
 *  request diffMap was built from, so the comment dot posts to the right one.
 *  Returns null when the field isn't currently diffed. */
export function getFieldDiff(
  diffMap: Map<string, FieldChange>,
  path: string,
  requestId: string,
  fieldHistory: Map<string, FieldChange[]> = new Map(),
  audience: 'admin' | 'brand' = 'admin',
  currency?: string | null,
) {
  const change = diffMap.get(path);
  if (!change) return null;
  return {
    change,
    statusBadge: <ReviewStatusBadge status={change.status} audience={audience} submittedByRole={change.submitted_by_role} />,
    value: <DiffValue path={path} oldValue={change.old_value} newValue={change.new_value} currency={currency} />,
    pills: <DiffPills path={path} oldValue={change.old_value} newValue={change.new_value} currency={currency} />,
    commentDot: (
      <CommentThreadPopover
        requestId={requestId}
        fieldPath={path}
        comments={change.comments ?? []}
        status={change.status}
        history={fieldHistory.get(path) ?? []}
        oldValue={change.old_value}
        newValue={change.new_value}
        submittedByRole={change.submitted_by_role}
        currency={currency}
      />
    ),
  };
}

/** Status badge + commentDot — the standard label-row decoration for every
 *  diffed field (here and in CampaignDetailsTab's hand-built label rows). */
function diffDecoration(diff: ReturnType<typeof getFieldDiff>) {
  if (!diff) return undefined;
  return (
    <>
      {diff.statusBadge}
      {diff.commentDot}
    </>
  );
}

/** Drop-in Field wrapper for the admin read layout: renders the live
 *  `children` normally, or swaps in the old→new diff + badge + comment dot
 *  when `path` has a diffable change on `diffMap`. */
export function DiffField({
  path,
  label,
  diffMap,
  requestId,
  fieldHistory,
  audience,
  currency,
  children,
}: {
  path: string;
  label: string;
  diffMap: Map<string, FieldChange>;
  requestId: string;
  fieldHistory?: Map<string, FieldChange[]>;
  audience?: 'admin' | 'brand';
  currency?: string | null;
  children: React.ReactNode;
}) {
  const diff = getFieldDiff(diffMap, path, requestId, fieldHistory, audience, currency);
  return (
    <Field label={label} id={`field-${path}`} badge={diffDecoration(diff)}>
      {diff ? diff.value : children}
    </Field>
  );
}

/** Drop-in PillField wrapper — same idea as DiffField, but swaps in the
 *  kept/removed/added pill row via PillField's `override` slot. */
export function DiffPillField({
  path,
  label,
  diffMap,
  requestId,
  fieldHistory,
  audience,
  currency,
  values,
}: {
  path: string;
  label: string;
  diffMap: Map<string, FieldChange>;
  requestId: string;
  fieldHistory?: Map<string, FieldChange[]>;
  audience?: 'admin' | 'brand';
  currency?: string | null;
  values: string[];
}) {
  const diff = getFieldDiff(diffMap, path, requestId, fieldHistory, audience, currency);
  return (
    <PillField label={label} id={`field-${path}`} badge={diffDecoration(diff)} values={values} override={diff?.pills} />
  );
}
